import { useMsal } from "@azure/msal-react";
import { useEffect, useState, useRef } from "react";
import ApiProvider from "../../apiProvider";
import { Spinner } from "react-bootstrap";
import "./PDFViewer.css";
import * as pdfjs from "pdfjs-dist";
import * as Icon from "react-bootstrap-icons";

const MobilePDFViewer = ({ fileName, setFileName }) => {
    const { instance, accounts } = useMsal();
    const apiProvider = new ApiProvider(instance, accounts[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [pdfDocument, setPdfDocument] = useState();
    const [pdfDocumentData, setPdfDocumentData] = useState();
    const [pageIndex, setPageIndex] = useState(1);
    const [pdfPage, setPdfPage] = useState();
    var onDocumentLoadSuccess = null;
    var onDocumentLoadFail = null;
    var onPageLoadSuccess = null;
    var onPageLoadFail = null;
    var onPageRenderSuccess = null;
    const onPageRenderFail = null;
    const onDocumentLoadSuccessRef = useRef(onDocumentLoadSuccess);
    const onDocumentLoadFailRef = useRef(onDocumentLoadFail);
    const onPageLoadSuccessRef = useRef(onPageLoadSuccess);
    const onPageLoadFailRef = useRef(onPageLoadFail);
    const onPageRenderSuccessRef = useRef(onPageRenderSuccess);
    const onPageRenderFailRef = useRef(onPageRenderFail);

    const renderTask = useRef(null);
    const canvasRef = useRef(null);
    var scale = 0.75;

    // assign callbacks to refs to avoid redrawing
    useEffect(() => {
        onDocumentLoadSuccessRef.current = onDocumentLoadSuccess;
    }, [onDocumentLoadSuccess]);

    useEffect(() => {
        onDocumentLoadFailRef.current = onDocumentLoadFail;
    }, [onDocumentLoadFail]);

    useEffect(() => {
        onPageLoadSuccessRef.current = onPageLoadSuccess;
    }, [onPageLoadSuccess]);

    useEffect(() => {
        onPageLoadFailRef.current = onPageLoadFail;
    }, [onPageLoadFail]);

    useEffect(() => {
        onPageRenderSuccessRef.current = onPageRenderSuccess;
    }, [onPageRenderSuccess]);

    useEffect(() => {
        onPageRenderFailRef.current = onPageRenderFail;
    }, [onPageRenderFail]);

    useEffect(() => {
        pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
    });

    useEffect(() => {
        async function getData() {
            if (!fileName) return;

            setIsLoading(true);
            let result = await apiProvider.Get(
                fileName,
                null,
                "application/pdf",
                false
            );
            setPdfDocumentData(result);
            pdfjs
                .getDocument({ data: convertDataURIToBinary(result) })
                .promise.then((loadedPdfDocument) => {
                    setPdfDocument(loadedPdfDocument);
                });
            setIsLoading(false);
        }

        getData();
    }, [fileName]);

    useEffect(() => {
        // draw a page of the pdf
        const drawPDF = (page) => {
            // Because this page's rotation option overwrites pdf default rotation value,
            // calculating page rotation option value from pdf default and this component prop rotate.
            const dpRatio = window.devicePixelRatio;
            const adjustedScale = scale * dpRatio;
            const viewport = page.getViewport({ scale: adjustedScale });
            const canvasEl = canvasRef.current;

            if (!canvasEl) {
                return;
            }

            const canvasContext = canvasEl.getContext("2d");
            if (!canvasContext) {
                return;
            }

            canvasEl.style.width = `${viewport.width / dpRatio}px`;
            canvasEl.style.height = `${viewport.height / dpRatio}px`;
            canvasEl.height = viewport.height;
            canvasEl.width = viewport.width;

            // if previous render isn't done yet, we cancel it
            if (renderTask.current) {
                renderTask.current.cancel();
                return;
            }

            renderTask.current = page.render({
                canvasContext,
                viewport,
            });

            return renderTask.current.promise.then(
                () => {
                    renderTask.current = null;
                },
                (reason) => {
                    renderTask.current = null;
                    drawPDF(page);
                }
            );
        };

        if (pdfDocument) {
            pdfDocument.getPage(pageIndex).then((loadedPdfPage) => {
                setPdfPage(loadedPdfPage);
                drawPDF(loadedPdfPage);
            });
        }
    }, [canvasRef, pageIndex, pdfDocument, scale]);

    function convertDataURIToBinary(dataURI) {
        var raw = window.atob(dataURI);
        var rawLength = raw.length;
        var array = new Uint8Array(new ArrayBuffer(rawLength));

        for (var i = 0; i < rawLength; i++) {
            array[i] = raw.charCodeAt(i);
        }
        return array;
    }

    function downloadFile() {
        var link = document.createElement("a");
        var raw = convertDataURIToBinary(pdfDocumentData);
        const file = new Blob([raw], { type: "application/octet-stream" });

        // process to auto download it
        const fileURL = URL.createObjectURL(file);
        link.download = fileName + ".pdf";
        link.target = "_self";
        link.href = fileURL;
        document.body.appendChild(link);
        link.click();
    }

    return (
        <>
            {Boolean(
                pdfDocument &&
                    pdfDocument.numPages &&
                    fileName !== undefined &&
                    fileName !== ""
            ) ? (
                <>
                    <div className="curtain"></div>
                    <div className="mobile-pdf-viewer">
                        <div className="control-wrapper">
                            <button
                                className="btn btn-link cds-link"
                                onClick={() => setFileName("")}
                            >
                                Close File
                            </button>
                        </div>
                        <div className="canvas-wrapper">
                            {isLoading ? (
                                <div className="spinner">
                                    <Spinner></Spinner>
                                    <p>Loading...</p>
                                </div>
                            ) : (
                                <div className="">
                                    {Boolean(
                                        pdfDocument && pdfDocument.numPages
                                    ) && (
                                        <div className="paginator-wrapper mb-3">
                                            <button
                                                className="btn btn-outline-primary"
                                                disabled={pageIndex === 1}
                                                onClick={() =>
                                                    setPageIndex(pageIndex - 1)
                                                }
                                            >
                                                <Icon.ArrowLeft className="icon" />
                                            </button>
                                            <button
                                                className="btn btn-outline-primary mx-4"
                                                onClick={() => downloadFile()}
                                            >
                                                <Icon.Download className="icon" />
                                            </button>
                                            <button
                                                className="btn btn-outline-primary"
                                                disabled={
                                                    pageIndex ===
                                                    pdfDocument.numPages
                                                }
                                                onClick={() =>
                                                    setPageIndex(pageIndex + 1)
                                                }
                                            >
                                                <Icon.ArrowRight className="icon" />
                                            </button>
                                        </div>
                                    )}
                                    <canvas ref={canvasRef} />
                                </div>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <></>
            )}
        </>
    );
};

export default MobilePDFViewer;
